import {
  ArrowBack as ArrowBackIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { formatarCPF, validarCPF, validarNomeUsuario, validarSenha } from '../../utils/validations';

const UsuarioForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: usuarioLogado, userRoles } = useAuth();
  const isEditando = !!id;

  const [formData, setFormData] = useState({
    nome: '',
    nome_de_usuario: '',
    cpf: '',
    senha: '',
    foto_perfil: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [carregando, setCarregando] = useState(isEditando);
  const [error, setError] = useState('');
  const [erros, setErros] = useState({});
  
  // Estados para roles
  const [rolesDisponiveis, setRolesDisponiveis] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [rolesUsuario, setRolesUsuario] = useState([]);
  const [rolesSelecionadas, setRolesSelecionadas] = useState(new Set());
  const [carregandoRoles, setCarregandoRoles] = useState(false);

  useEffect(() => {
    if (isEditando) {
      carregarUsuario();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const carregarUsuario = async () => {
    setCarregando(true);
    setCarregandoRoles(true);
    setError('');
    
    try {
      // 1. Carregar dados do usuário
      const usuario = await adminService.buscarUsuario(id);
      setFormData({
        nome: usuario.nome || '',
        nome_de_usuario: usuario.nome_de_usuario || '',
        cpf: formatarCPF(usuario.cpf || ''),
        senha: '',
        foto_perfil: usuario.foto_perfil || ''
      });

      // 2. Carregar todas as roles disponíveis
      const dadosRoles = await adminService.listarRoles(1, 100);
      setRolesDisponiveis(dadosRoles.roles || []);

      // 3. Carregar roles do usuário
      try {
        const dadosRolesUsuario = await adminService.listarRolesUsuario(id);
        setRolesUsuario(dadosRolesUsuario.roles || []);

        // 4. Marcar roles que o usuário já possui (usar identificador UUID)
        const rolesAtuais = new Set();
        (dadosRolesUsuario.roles || []).forEach(r => {
          // Pode vir como r.identificador ou r.role?.identificador
          const identificador = r.identificador || r.role?.identificador;
          if (identificador) {
            rolesAtuais.add(identificador);
          } else {
            console.warn('[UsuarioForm] Role sem identificador UUID:', r);
          }
        });
        
        // Log para debug
        if (process.env.NODE_ENV === 'development') {
          console.log('[UsuarioForm] Roles carregadas:', {
            totalRoles: dadosRolesUsuario.roles?.length || 0,
            rolesComIdentificador: rolesAtuais.size,
            rolesAtuais: Array.from(rolesAtuais),
            rolesDetalhes: dadosRolesUsuario.roles
          });
        }
        
        setRolesSelecionadas(rolesAtuais);
      } catch (rolesError) {
        // Se falhar ao carregar roles, continuar sem roles (não bloquear edição)
        console.warn('Erro ao carregar roles do usuário:', rolesError);
        setRolesUsuario([]);
        setRolesSelecionadas(new Set());
        // Não definir como erro fatal, apenas avisar
        if (rolesError.message.includes('Token inválido') || rolesError.message.includes('Sem permissão')) {
          setError(rolesError.message);
        }
      }
    } catch (err) {
      setError(err.message || 'Erro ao carregar dados do usuário');
    } finally {
      setCarregando(false);
      setCarregandoRoles(false);
    }
  };

  const validarFormulario = () => {
    const novosErros = {};

    if (!formData.nome || formData.nome.length < 2) {
      novosErros.nome = 'Nome deve ter no mínimo 2 caracteres';
    }

    const validacaoUsuario = validarNomeUsuario(formData.nome_de_usuario);
    if (!validacaoUsuario.valido) {
      novosErros.nome_de_usuario = validacaoUsuario.mensagem;
    }

    const validacaoCPF = validarCPF(formData.cpf);
    if (!validacaoCPF.valido) {
      novosErros.cpf = validacaoCPF.mensagem;
    }

    if (!isEditando) {
      const validacaoSenha = validarSenha(formData.senha);
      if (!validacaoSenha.valido) {
        novosErros.senha = validacaoSenha.mensagem;
      }
    } else if (formData.senha) {
      const validacaoSenha = validarSenha(formData.senha);
      if (!validacaoSenha.valido) {
        novosErros.senha = validacaoSenha.mensagem;
      }
    }

    if (formData.foto_perfil && formData.foto_perfil.length > 500) {
      novosErros.foto_perfil = 'URL da foto deve ter no máximo 500 caracteres';
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const toggleRole = (roleIdentificador) => {
    // roleIdentificador é o UUID da role (campo 'identificador')
    const novasSelecionadas = new Set(rolesSelecionadas);
    
    if (novasSelecionadas.has(roleIdentificador)) {
      // Desmarcar
      novasSelecionadas.delete(roleIdentificador);
    } else {
      // Marcar
      novasSelecionadas.add(roleIdentificador);
    }
    
    setRolesSelecionadas(novasSelecionadas);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);
    try {
      // 1. Atualizar dados do usuário
      const dados = {
        nome: formData.nome,
        nome_de_usuario: formData.nome_de_usuario,
        cpf: formData.cpf.replace(/\D/g, ''),
        foto_perfil: formData.foto_perfil || undefined
      };

      if (!isEditando || formData.senha) {
        dados.senha = formData.senha;
      }

      if (isEditando) {
        // Verificar se o usuário logado tem permissão
        const isAdmin = userRoles.some(role => role.toLowerCase() === 'admin');
        const isEditandoProprioUsuario = usuarioLogado?.id?.toString() === id;
        
        // Log para debug
        if (process.env.NODE_ENV === 'development') {
          console.log('[UsuarioForm] Verificando permissões:', {
            usuarioLogadoId: usuarioLogado?.id,
            usuarioEditandoId: id,
            isEditandoProprioUsuario,
            userRoles,
            isAdmin
          });
        }
        
        // Avisar se não tem role admin
        if (!isAdmin) {
          setError('Você precisa ter a role "admin" para atualizar usuários. Suas roles atuais: ' + (userRoles.length > 0 ? userRoles.join(', ') : 'nenhuma'));
          setLoading(false);
          return;
        }
        
        // Atualizar dados do usuário
        try {
          await adminService.editarUsuario(id, dados);
        } catch (usuarioError) {
          // Mensagem de erro mais detalhada
          let mensagemErro = usuarioError.message;
          
          if (usuarioError.status === 403 || usuarioError.message.includes('Sem permissão')) {
            // Erro 403 - Sem permissão
            if (isEditandoProprioUsuario) {
              mensagemErro = 'Você não pode atualizar seu próprio usuário através desta interface. Use a página de perfil.';
            } else {
              mensagemErro = `Sem permissão para atualizar este usuário (ID: ${id}).\n\n` +
                `Possíveis causas:\n` +
                `• O backend tem regras específicas que impedem a atualização deste usuário\n` +
                `• O usuário pode ter características especiais que requerem permissões adicionais\n` +
                `• Verifique as regras de negócio no backend\n\n` +
                `Você está logado como usuário ID ${usuarioLogado?.id} com roles: ${userRoles.join(', ')}`;
              
              // Log detalhado para debug
              if (process.env.NODE_ENV === 'development') {
                console.error('[UsuarioForm] Erro 403 detalhado:', {
                  usuarioLogado: {
                    id: usuarioLogado?.id,
                    nome: usuarioLogado?.nome,
                    roles: userRoles
                  },
                  usuarioEditando: {
                    id: id
                  },
                  erro: usuarioError,
                  detalhes: usuarioError.detalhes
                });
              }
            }
          }
          
          setError(mensagemErro);
          setLoading(false);
          return;
        }
        
        // 2. Atualizar roles do usuário (apenas ao editar)
        // Converter Set para Array de UUIDs
        const rolesArray = Array.from(rolesSelecionadas);
        
        // Log para debug
        if (process.env.NODE_ENV === 'development') {
          console.log('[UsuarioForm] Atualizando roles:', {
            usuarioId: id,
            rolesCount: rolesArray.length,
            roles: rolesArray
          });
        }
        
        try {
          await adminService.atualizarRolesUsuario(id, rolesArray);
        } catch (rolesError) {
          // Se falhar ao atualizar roles, mostrar erro específico
          setError(`Usuário atualizado, mas erro ao atualizar roles: ${rolesError.message}`);
          setLoading(false);
          return;
        }
      } else {
        await adminService.criarUsuario(dados);
      }

      navigate('/admin/usuarios');
    } catch (err) {
      setError(err.message || 'Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleCPFChange = (e) => {
    const formatted = formatarCPF(e.target.value);
    setFormData({ ...formData, cpf: formatted });
  };

  if (carregando) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/usuarios')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {isEditando ? 'Editar Usuário' : 'Criar Usuário'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            margin="normal"
            required
            error={!!erros.nome}
            helperText={erros.nome}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Nome de Usuário"
            value={formData.nome_de_usuario}
            onChange={(e) => setFormData({ ...formData, nome_de_usuario: e.target.value })}
            margin="normal"
            required
            error={!!erros.nome_de_usuario}
            helperText={erros.nome_de_usuario}
            disabled={loading || isEditando}
          />

          <TextField
            fullWidth
            label="CPF"
            value={formData.cpf}
            onChange={handleCPFChange}
            margin="normal"
            required
            error={!!erros.cpf}
            helperText={erros.cpf}
            disabled={loading || isEditando}
            inputProps={{ maxLength: 14 }}
          />

          <TextField
            fullWidth
            label={isEditando ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
            type={showPassword ? 'text' : 'password'}
            value={formData.senha}
            onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
            margin="normal"
            required={!isEditando}
            error={!!erros.senha}
            helperText={erros.senha}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Foto de Perfil (URL)"
            value={formData.foto_perfil}
            onChange={(e) => setFormData({ ...formData, foto_perfil: e.target.value })}
            margin="normal"
            error={!!erros.foto_perfil}
            helperText={erros.foto_perfil || 'URL opcional para foto de perfil'}
            disabled={loading}
          />

          {/* Seção de Roles - apenas ao editar */}
          {isEditando && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Roles e Permissões
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Selecione as roles que este usuário deve possuir:
              </Typography>

              {carregandoRoles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : rolesDisponiveis.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Nenhuma role disponível. Crie roles primeiro.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {rolesDisponiveis
                      .filter(role => {
                        // Filtrar apenas roles com identificador válido
                        if (!role.identificador) {
                          console.warn('[UsuarioForm] Role sem identificador UUID ignorada:', role);
                          return false;
                        }
                        return true;
                      })
                      .map((role) => {
                        const estaSelecionada = rolesSelecionadas.has(role.identificador);
                        
                        return (
                          <Grid item xs={12} sm={6} md={4} key={role.id}>
                            <Card
                              sx={{
                                border: `2px solid ${estaSelecionada ? 'primary.main' : 'divider'}`,
                                backgroundColor: estaSelecionada ? 'action.selected' : 'background.paper',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  boxShadow: 2
                                }
                              }}
                              onClick={() => {
                                if (role.identificador) {
                                  toggleRole(role.identificador);
                                } else {
                                  console.error('[UsuarioForm] Tentativa de toggle em role sem identificador:', role);
                                }
                              }}
                            >
                              <CardContent>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={estaSelecionada}
                                      onChange={() => {
                                        if (role.identificador) {
                                          toggleRole(role.identificador);
                                        }
                                      }}
                                      disabled={loading || !role.identificador}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {role.nome}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        {role.descricao}
                                      </Typography>
                                      {process.env.NODE_ENV === 'development' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                          ID: {role.identificador?.substring(0, 8)}...
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, width: '100%' }}
                                />
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                  </Grid>

                  {/* Resumo */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      mb: 2
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Roles selecionadas:</strong> {rolesSelecionadas.size} de {rolesDisponiveis.length}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Salvar'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/usuarios')}
              disabled={loading}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default UsuarioForm;

